import styled from '@emotion/styled';
import React, { useState } from 'react';
import { keyframes } from '@emotion/react';
import { graphql } from 'gatsby';
import PropTypes from 'prop-types';

import { containerStyles } from '../../../styles/layout';
import { headers } from '../../../styles/type';
import { colors } from '../../../theme/colors';
import { rem } from '../../../utilities/style';
import { media } from '../../../theme/media';

import { ReactComponent as SecurityTabsLines } from '../../../images/icons/security-tabs-lines.svg';
import VerticalTab from './VerticalTab';
import Image from '../../Image';
import TextRenderer from '../../TextRenderer';

const Container = styled.div`
  padding: ${rem(32)} 0;
  background-color : ${({ backgroundColor }) => (backgroundColor === 'Blue' ? colors.blue['50'] : colors.white)};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    background-color: ${colors.blue['800']};
  `}

  ${media('>tiny')} {
    padding: ${rem(40)} 0 ${rem(30)} 0;
  }
`;

const ContentWrapper = styled.div`
${containerStyles.regular}
`;

const Headline = styled.h2`
  ${headers.h2}
  text-align: center;
  margin-bottom: ${rem(32)};
  color: ${colors.blue['800']};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.blue['100']};
  `}
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  ${media('>smedium')}{
    flex-direction: row;
    gap: ${rem(24)};
  }
`;

const Menu = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;

  ${media('>smedium')}{
    flex-direction: row;
  }
`;

const MenuItems = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${rem(4)};

  ${media('>tiny')}{
    gap: ${rem(20)};
  }

  ${media('>smedium')}{
    width: ${rem(160)};
    grid-template-columns: 1fr;
  }
`;

const MenuItem = styled.button`
  display: flex;
  border: none;
  border-radius: ${rem(10)};
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: ${rem(8)};
  background-color: transparent;
  color: ${colors.blue['800']};
  opacity: 0.6;
  transition: opacity 0.2s ease-in;
  filter: saturate(25%);
  cursor: pointer;

  ${media('<=tiny')}{
    svg {
      width: ${rem(40)};
    }
  }

  ${media('>tiny')}{
    padding: ${rem(16)};
  }

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
      opacity: 1;
      filter: none;
      color: ${colors.gray['600']};
  `}

  ${({ active, theme }) => active && `
    filter: none;
    opacity: 1;
    background-color: ${colors.white};
    box-shadow: 0 ${rem(2)} ${rem(8)} rgba(0, 0, 0, 0.08),
                0 ${rem(8)} ${rem(40)} rgba(0, 0, 0, 0.08);
    ${theme.pageOverrides?.includes('Dark Mode') && `
      color: ${colors.blue['100']};
      background-color: ${colors.blue['800']};
      border: 1px solid ${colors.blue['700']};
      box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.16), 0px 2px 8px 0px rgba(0, 0, 0, 0.16);
    `}
  `}
`;

const MenuItemIcon = styled(Image)`
  max-height: ${rem(50)};
`;

const MenuItemText = styled.p`
  ${headers.h6}
  margin-top: ${rem(8)};

  ${media('<=small')}{
    margin-top: 0;
    font-size: ${rem(14)};
  }

  ${media('<=tiny')}{
    font-size: ${rem(12)};
  }
`;

const LinesContainer = styled.div`

  ${media('<=smedium')}{
    position: relative;
    width: 70%;
    aspect-ratio: 285 / 50;
    mask-image: linear-gradient(#000, #0000);

    svg {
      position: absolute;
      top: 80%;
      left: 50%;
      transform: translate(-50%, -50%) rotate(90deg) scaleY(-1) scale(calc(99 / 285));
      transform-origin: center;
      width: 100%;
      height: auto;
    }
  }

  ${media('>smedium')}{
    display: block;
    flex-shrink: 0;
    width: ${rem(99)};
    mask-image: linear-gradient(90deg, #000, #0000);
  }
`;

const dash = keyframes`
  100% {
    stroke-dashoffset: 0;
  }
`;

const StyledLines = styled(SecurityTabsLines)`

  path {
    stroke-width: 5;
    stroke-dasharray: 10;
    stroke-dashoffset: 20;

    ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
      stroke: ${colors.gray['700']};
    `}

    ${media('>smedium')} {
      stroke-width: 2;
      stroke-dasharray: 4;
      stroke-dashoffset: 8;
    }
  }

  .line {
    stroke: ${colors.blue['400']};
    animation: ${dash} 1s linear infinite;
  }

  .line-${({ index }) => index} {
    stroke: ${colors.blue['400']};
    animation: ${dash} 1s linear infinite;
  }
`;

const Tabs = styled.div`

`;

const VerticalTabs = ({ headline, verticalTabs, backgroundColor }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const changeTab = (tabIndex) => {
    setActiveIndex(tabIndex);
  };

  return (
    <Container backgroundColor={backgroundColor}>
      <ContentWrapper>
        {headline && (
          <Headline>
            <TextRenderer text={headline} />
          </Headline>
        )}
        <Content>
          <Menu>
            <MenuItems>
              {verticalTabs && verticalTabs.map(({ tabText, tabIcon }, index) => (
                <MenuItem
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  onClick={() => changeTab(index)}
                  active={activeIndex === index}
                >
                  {tabIcon && (
                    <MenuItemIcon objectFit="contain" image={tabIcon} />
                  )}
                  {tabText && (
                    <MenuItemText>
                      <TextRenderer text={tabText} />
                    </MenuItemText>
                  )}
                </MenuItem>
              ))}
            </MenuItems>
            <LinesContainer>
              <StyledLines index={activeIndex} />
            </LinesContainer>
          </Menu>
          <Tabs>
            {verticalTabs && verticalTabs.map((verticalTab, index) => (
              <VerticalTab
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                active={activeIndex === index}
                headline={verticalTab.headline}
                text={verticalTab.text}
                primaryCtaText={verticalTab.primaryCtaText}
                primaryCtaLink={verticalTab.primaryCtaLink}
                image={verticalTab.image}
              />
            ))}
          </Tabs>
        </Content>
      </ContentWrapper>
    </Container>
  );
};

VerticalTabs.propTypes = {
  headline: PropTypes.string,
  verticalTabs: PropTypes.arrayOf(
    PropTypes.shape(VerticalTab.propTypes)
  ),
  backgroundColor: PropTypes.oneOf(['Blue', 'White'])
};

export const query = graphql`
  fragment VerticalTabsFragment on ContentfulVerticalTabs {
    __typename
    headline
    verticalTabs {
      ...VerticalTabFragment
    }
    backgroundColor
  }
`;

export default VerticalTabs;
